/* eslint react/forbid-prop-types: 0 */
import PropTypes from 'prop-types';
import ConstituentTypes from '@/constants/constituents/DonorTypes';
import AddressPropType from '@/lib/types/AddressPropType';
import DonorUserPropType from '@/lib/types/DonorUserPropType';
import DonorWorkPropType from '@/lib/types/DonorWorkPropType';
import EmailPropType from '@/lib/types/EmailPropType';
import PhonePropType from '@/lib/types/PhonePropType';
import SocialPropType from '@/lib/types/SocialPropType';

const DonorTypes = PropTypes.oneOf(Object.values(ConstituentTypes));

const ConstituentConnectionTypes = {
  AFFILIATIONS: 'affiliations',
  CONNECTIONS: 'connections',
  CONTACTS: 'contacts',
  EMPLOYEES: 'employees',
  EMPLOYERS: 'employers',
  HOUSEHOLD: 'household',
};

const ConstituentSectionTypes = {
  AFFILIATIONS: ConstituentConnectionTypes.AFFILIATIONS,
  EMPLOYEES: ConstituentConnectionTypes.EMPLOYEES,
  CONNECTIONS: ConstituentConnectionTypes.CONNECTIONS,
  HOUSEHOLD: ConstituentConnectionTypes.HOUSEHOLD,
  PERSONAL_INFORMATION: 'personalInformation',
  COMPANY_INFORMATION: 'companyInformation',
  CONTACT_INFORMATION: 'contactInformation',
  WORK_INFORMATION: 'workInformation',
  GIVING_SUMMARY: 'givingSummary',
};

const DonorRelationType = PropTypes.shape({
  relationshipId: PropTypes.string,
  relatedId: PropTypes.string,
  relatedResourceEnum: PropTypes.string,
  relationName: PropTypes.string,
  relationLabel: PropTypes.string,
  relationStatus: PropTypes.string,
  relationStartDateString: PropTypes.string,
  relationEndDateString: PropTypes.string,
  relationData: PropTypes.object,
});

const DonorType = PropTypes.shape({
  acquaintances: PropTypes.arrayOf(PropTypes.shape(DonorUserPropType)),
  addresses: PropTypes.arrayOf(PropTypes.shape(AddressPropType)),
  affiliations: PropTypes.arrayOf(PropTypes.shape(DonorWorkPropType)),
  avatar: PropTypes.string,
  connections: PropTypes.arrayOf(PropTypes.shape(DonorUserPropType)),
  contacts: PropTypes.arrayOf(PropTypes.shape(DonorUserPropType)),
  created: PropTypes.string,
  deleted: PropTypes.bool,
  displayName: PropTypes.string,
  donationPlans: PropTypes.arrayOf(
    // TODO(jon): The commented out parts create a circular dependency. We need
    // to revisit consolidating these enum values in one file.
    PropTypes.shape({
      // cadence: PropTypes.oneOf(values(RECURRING_CADENCES_ENUM)),
      cadence: PropTypes.string,
      endDate: PropTypes.string,
      extPlanId: PropTypes.string,
      extSource: PropTypes.string,
      planId: PropTypes.string,
      recurringAmount: PropTypes.number,
      startDate: PropTypes.string,
      // status: PropTypes.oneOf(values(DONATION_PLAN_STATUS_ENUM)),
      status: PropTypes.string,
    }),
  ),
  donorId: PropTypes.string,
  donorType: DonorTypes,
  emails: PropTypes.arrayOf(PropTypes.shape(EmailPropType)),
  employees: PropTypes.arrayOf(PropTypes.shape(DonorUserPropType)),
  employers: PropTypes.arrayOf(DonorRelationType),
  firstName: PropTypes.string,
  fromN: PropTypes.number,
  household: PropTypes.arrayOf(PropTypes.shape(DonorUserPropType)),
  lastDonationDate: PropTypes.string,
  lastName: PropTypes.string,
  lastYearSum: PropTypes.number,
  lifetimeCount: PropTypes.number,
  lifetimeSum: PropTypes.number,
  note1: PropTypes.string,
  note2: PropTypes.string,
  note3: PropTypes.string,
  ownerOrgId: PropTypes.string,
  phones: PropTypes.arrayOf(PropTypes.shape(PhonePropType)),
  planned: PropTypes.number,
  primaryEmail: PropTypes.string,
  primaryPhone: PropTypes.string,
  referredBy: PropTypes.shape({
    displayName: PropTypes.string,
    userId: PropTypes.string,
  }),
  summary: PropTypes.shape({
    created: PropTypes.string,
    deleted: PropTypes.bool,
    displayName: PropTypes.string,
    donorId: PropTypes.string,
    donorType: DonorTypes,
    ownerOrgId: PropTypes.string,
    primaryEmail: PropTypes.string,
    primaryPhone: PropTypes.string,
  }),
  thisYearCount: PropTypes.number,
  thisYearSum: PropTypes.number,
  toN: PropTypes.number,
  updated: PropTypes.string,
  work: PropTypes.arrayOf(PropTypes.shape(DonorWorkPropType)),
  ...SocialPropType,
});

const DonorConfigType = {
  fieldToExtract: PropTypes.string,
  icon: PropTypes.node,
  key: PropTypes.string.isRequired,
  labels: PropTypes.arrayOf(PropTypes.string),
  mapper: PropTypes.func,
  noDataLabel: PropTypes.string,
  path: PropTypes.string.isRequired,
  usePrimary: PropTypes.bool,
};

const GapType = {
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  vertical: PropTypes.bool,
};

// TODO: fix once it's known
const ProfileDescriptorType = DonorType;

const GroupType = PropTypes.arrayOf(
  PropTypes.shape({
    // groupDisplayName behaves as a uniq identifier based on the api response
    groupDisplayName: PropTypes.string.isRequired,
    // groupId is absent in case of team members
    groupId: PropTypes.string,
  }),
);

const CompanyTeamMember = PropTypes.shape({
  email: PropTypes.string,
  firstName: PropTypes.string,
  groups: GroupType,
  inviteId: PropTypes.string,
  lastName: PropTypes.string,
  lastVisit: PropTypes.string,
  userId: PropTypes.string,
});

const CompanyTeamMemberStatus = (
  props: {
    status: {
      dataLoaded?: boolean;
      isFetching?: boolean;
      error?: boolean;
    };
  },
  propName: 'status',
  componentName: string,
) => {
  const status = props[propName];

  if (
    typeof status?.dataLoaded === 'undefined' ||
    typeof status?.isFetching === 'undefined' ||
    typeof status?.error === 'undefined'
  ) {
    throw Error(`Invalid prop ${propName} supplied to ${componentName}`);
  }
};

const CustomTableFilterProps = PropTypes.shape({
  order: PropTypes.oneOf(['asc', 'desc']),
  orderBy: PropTypes.string,
  searchValue: PropTypes.string,
});

const CustomTableProps = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      field: PropTypes.string,
      align: PropTypes.oneOf(['left', 'right', 'center']),
      getComparableValue: PropTypes.func,
      headerName: PropTypes.string.isRequired,
      width: PropTypes.number,
      renderCell: PropTypes.func,
      sortable: PropTypes.bool,
      disableClickEventBubbling: PropTypes.bool,
      valueFormatter: PropTypes.func,
      style: PropTypes.shape({}),
    }),
  ),
  data: PropTypes.arrayOf(PropTypes.shape({})),
  filter: CustomTableFilterProps,
  idField: PropTypes.string.isRequired,
  onRowClick: PropTypes.func,
  setFilter: PropTypes.func,
  tableStyles: PropTypes.shape({}),
};

const TextSelectFieldValueType = PropTypes.shape({
  icon: PropTypes.string,
  text: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

export {
  CompanyTeamMember,
  CompanyTeamMemberStatus,
  ConstituentConnectionTypes,
  ConstituentSectionTypes,
  CustomTableFilterProps,
  CustomTableProps,
  DonorConfigType,
  DonorRelationType,
  DonorType,
  DonorTypes,
  GapType,
  GroupType,
  ProfileDescriptorType,
  TextSelectFieldValueType,
};

export { default as TaskItemPropType } from './TaskItemPropType';
